import { useEffect, useState } from 'react';
import { cacheImage, getStoredImageSrc, getSessionImageSrc } from '../lib/images/imageCache';
import {
  EFFECTIVE_CONNECTIVITY,
  getEffectiveConnectivity,
  isEffectivelyOnline,
  subscribeEffectiveConnectivity,
} from '../lib/network/effectiveConnectivity';

/**
 * The src for an <img>, offline-safe (see lib/images/imageCache.js).
 *
 * Order: a copy already opened this session is used synchronously (no flash of placeholder on a list that mounts
 * and unmounts cards); else, online, the plain URL at once so the browser loads it the way it always has - lazily,
 * progressively - while the store is checked and a stored copy swaps in if there is one; `onLoad` stores the bytes
 * for next time (a 304 against the CDN, not a second download). With no copy and no service the src is null, so the
 * component shows its placeholder instead of the broken-image glyph, and `retryKey` changes when service comes back
 * so it tries again.
 *
 * Returns { src, onLoad, retryKey }.
 */
export function useCachedImageSrc(url) {
  const initial = (u) => {
    if (!u) return { url: u, src: null, resolved: true };
    const session = getSessionImageSrc(u);
    if (session) return { url: u, src: session, resolved: true };
    return { url: u, src: isEffectivelyOnline() ? u : null, resolved: false };
  };
  const [state, setState] = useState(() => initial(url));
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let on = true;
    const first = initial(url);
    setState(first);
    if (first.resolved) return undefined;
    getStoredImageSrc(url).then((stored) => {
      if (!on) return;
      setState((prev) =>
        prev.url === url
          ? { url, src: stored ?? prev.src ?? (isEffectivelyOnline() ? url : null), resolved: true }
          : prev,
      );
    });
    return () => {
      on = false;
    };
  }, [url, retryKey]);

  // Service back (REACHABLE again, from unreachable OR from the two-bars-no-throughput state): a card that could
  // not load, or was held back, tries again.
  useEffect(() => {
    if (!url) return undefined;
    let was = getEffectiveConnectivity();
    return subscribeEffectiveConnectivity(() => {
      const now = getEffectiveConnectivity();
      if (now === EFFECTIVE_CONNECTIVITY.REACHABLE && was !== EFFECTIVE_CONNECTIVITY.REACHABLE)
        setRetryKey((k) => k + 1);
      was = now;
    });
  }, [url]);

  // Store the bytes only once the store has answered "not here": before that a stored copy may be about to swap in,
  // and fetching again would download a hero twice on a congested cell.
  const onLoad = () => {
    if (state.resolved && state.src === url) void cacheImage(url);
  };
  const src = state.url === url ? state.src : null;
  return { src, onLoad, retryKey, resolved: state.resolved && state.url === url };
}
